import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MentorQuestionValidationsSubTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function MentorQuestionValidationsSubTab() {
  const [validations, setValidations] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingValidation, setEditingValidation] = useState(null);
  const [formData, setFormData] = useState({
    mentor_id: '',
    topic_id: '',
    is_active: 1
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [validationsRes, mentorsRes, topicsRes] = await Promise.all([
        axios.get(`${API_URL}/api/mentor-question-validations`),
        axios.get(`${API_URL}/api/mentor-profiles`),
        axios.get(`${API_URL}/api/topics`)
      ]);
      setValidations(validationsRes.data);
      setMentors(mentorsRes.data);
      setTopics(topicsRes.data);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingValidation) {
        await axios.put(
          `${API_URL}/api/mentor-question-validations/${editingValidation.id}`,
          { is_active: Number(formData.is_active) }
        );
      } else {
        await axios.post(`${API_URL}/api/mentor-question-validations`, {
          mentor_id: Number(formData.mentor_id),
          topic_id: Number(formData.topic_id),
          is_active: Number(formData.is_active)
        });
      }
      setShowForm(false);
      setEditingValidation(null);
      setFormData({ mentor_id: '', topic_id: '', is_active: 1 });
      fetchData();
      setError(null);
    } catch (err) {
      setError('Ошибка при сохранении: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEdit = (validation) => {
    setEditingValidation(validation);
    setFormData({
      mentor_id: validation.mentor_id,
      topic_id: validation.topic_id,
      is_active: validation.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить эту запись валидации?')) {
      try {
        await axios.delete(`${API_URL}/api/mentor-question-validations/${id}`);
        fetchData();
      } catch (err) {
        setError('Ошибка при удалении: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingValidation(null);
    setFormData({ mentor_id: '', topic_id: '', is_active: 1 });
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="mentor-question-validations-sub-tab">
      <div className="tab-header">
        <h2>Валидации вопросов</h2>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + Добавить валидацию
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="form-container">
          <h3>{editingValidation ? 'Редактировать статус валидации' : 'Добавить валидацию'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="mentor_id">Ментор *</label>
              <select
                id="mentor_id"
                value={formData.mentor_id}
                onChange={(e) => setFormData({ ...formData, mentor_id: e.target.value })}
                required
                disabled={!!editingValidation}
              >
                <option value="">Выберите ментора</option>
                {mentors.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.first_name} {m.last_name || ''} ({m.login})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="topic_id">Тема *</label>
              <select
                id="topic_id"
                value={formData.topic_id}
                onChange={(e) => setFormData({ ...formData, topic_id: e.target.value })}
                required
                disabled={!!editingValidation}
              >
                <option value="">Выберите тему</option>
                {topics.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="is_active">Статус активности</label>
              <select
                id="is_active"
                value={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
              >
                <option value={1}>1 — активна</option>
                <option value={0}>0 — неактивна</option>
              </select>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingValidation ? 'Сохранить' : 'Создать'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Ментор</th>
              <th>Тема</th>
              <th>Активность</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {validations.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty-state">
                  Нет записей валидаций. Добавьте первую.
                </td>
              </tr>
            ) : (
              validations.map((v) => (
                <tr key={v.id}>
                  <td>{v.id}</td>
                  <td>
                    {v.mentor_first_name || ''} ({v.mentor_login || v.mentor_id})
                  </td>
                  <td>{v.topic_title || v.topic_id}</td>
                  <td>{v.is_active === 1 ? 'Активна (1)' : 'Неактивна (0)'}</td>
                  <td>
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(v)}
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(v.id)}
                      title="Удалить"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MentorQuestionValidationsSubTab;
